import React, { useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useDocumentMeta } from "@/seo/useDocumentMeta";

const PrivacyPolicy = () => {
  // Title, description and canonical for client-side navigation; the
  // prerendered HTML already carries them on a cold load.
  useDocumentMeta("/privacy-policy");

  // Ensure we scroll to top when directly accessing this page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-reelmatch-background">
      <Header />
      <main className="container mx-auto px-4 py-24">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-center">Privacy Policy</h1>
          
          <div className="text-sm text-gray-500 text-center mb-12">
            Last updated: September 28, 2026
          </div>

          <div className="space-y-4">
            <section className="bg-gray-50">
              <p className="text-xl text-gray-900 mb-4">
                Welcome to ReelMatch, a freemium app developed by Digital Space Agency UG. While our core services are 
                available at no cost, we want to be transparent about how we handle your information.
              </p>

              <div className="space-y-4">
                <p>
                  This Privacy Policy outlines our practices regarding the collection, use, and protection of your 
                  Personal Information when you use our Service. By choosing to use ReelMatch, you agree to these 
                  practices as described below.
                </p>

                <p>
                  We are committed to protecting your privacy and will only use your information to enhance your 
                  experience and improve our Service. Any information shared with us will be handled as described 
                  in this Privacy Policy and will not be shared with third parties except as outlined here.
                </p>
              </div>
            </section>

            <section>
              <h2>Information We Collect</h2>
              <div className="space-y-4">
                <p>
                  To provide you with the best possible experience, we collect certain personally identifiable 
                  information, including:
                </p>
                
                <ul className="list-disc pl-6 space-y-2">
                  <li>Name</li>
                  <li>Email Address</li>
                </ul>

                <p>
                  Additionally, our app utilizes trusted third-party services that may collect information 
                  to help us improve and secure our service. These partners include:
                </p>

                <div className="grid md:grid-cols-2 gap-4 mt-6">
                  <a 
                    href="https://www.google.com/policies/privacy/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
                  >
                    <span className="text-reelmatch-primary">Google Play Services</span>
                  </a>
                  <a 
                    href="https://firebase.google.com/policies/analytics" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
                  >
                    <span className="text-reelmatch-primary">Google Analytics for Firebase</span>
                  </a>
                  <a 
                    href="https://firebase.google.com/support/privacy/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
                  >
                    <span className="text-reelmatch-primary">Firebase Crashlytics</span>
                  </a>
                  <a 
                    href="https://www.revenuecat.com/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
                  >
                    <span className="text-reelmatch-primary">RevenueCat</span>
                  </a>
                </div>
              </div>
            </section>

            <section>
              <h2>Log Data</h2>
              <p>
                We want to inform you that whenever you use our Service, in a case of an error in the app we collect 
                data and information (through third-party products) on your phone called Log Data. This Log Data may 
                include information such as your device Internet Protocol ("IP") address, device name, operating 
                system version, the configuration of the app when utilizing our Service, the time and date of your 
                use of the Service, and other statistics.
              </p>
            </section>

            <section>
              <h2>Cookies</h2>
              <p>
                Cookies are files with a small amount of data that are commonly used as anonymous unique identifiers. 
                These are sent to your browser from the websites that you visit and are stored on your device's 
                internal memory.
              </p>

              <p>
                This Service does not use these "cookies" explicitly. However, the app may use third-party code and 
                libraries that use "cookies" to collect information and improve their services. You have the option 
                to either accept or refuse these cookies and know when a cookie is being sent to your device. If you 
                choose to refuse our cookies, you may not be able to use some portions of this Service.
              </p>
            </section>

            <section>
              <h2>Website analytics</h2>
              <p>
                This website (reelmatch.app) uses Google Analytics 4, provided by Google, to understand how visitors find and use the site, for example which pages are viewed and whether a visitor taps an App Store or Google Play button. Google Analytics is only loaded if you choose "Accept All" or turn on analytics cookies in the cookie banner. If you do, it sets first-party cookies (such as _ga) and sends usage data, including your IP address and device and browser information, to Google. We do not use this data to identify you personally.
              </p>
              <p>
                You can change your choice at any time using the "Cookie Settings" button at the bottom of the page. Learn how Google uses this data at{" "}
                <a
                  href="https://policies.google.com/technologies/partner-sites"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-reelmatch-primary"
                >
                  policies.google.com/technologies/partner-sites
                </a>
                .
              </p>
            </section>

            <section>
              <h2>Aggregated and anonymous statistics</h2>
              <p>
                We may publish statistics about how ReelMatch is used, for example the movies and shows most often matched, the genres people agree on most, or the average number of swipes before a first match. We may share these statistics on our website, in press materials and on social media.
              </p>
              <p>
                These statistics are calculated from activity across many users and are anonymous. They never include names, email addresses, or any other information that could identify you, your friends, or the individual titles you liked or skipped. We only publish a figure when it is based on enough people that no individual can be singled out.
              </p>
            </section>

            <section>
              <h2>Service Providers</h2>
              <p>We may employ third-party companies and individuals due to the following reasons:</p>
              <ul>
                <li>To facilitate our Service;</li>
                <li>To provide the Service on our behalf;</li>
                <li>To perform Service-related services; or</li>
                <li>To assist us in analyzing how our Service is used.</li>
              </ul>
              <p>
                We want to inform users of this Service that these third parties
                have access to your Personal Information. The reason is to
                perform the tasks assigned to them on our behalf. However, they
                are obligated not to disclose or use the information for any
                other purpose.
              </p>
            </section>

            <section>
              <h2>Links to Other Sites</h2>
              <p>
                This Service may contain links to other sites. If you click on a
                third-party link, you will be directed to that site. Note that
                these external sites are not operated by us. Therefore, we
                strongly advise you to review the Privacy Policy of these
                websites. We have no control over and assume no responsibility
                for the content, privacy policies, or practices of any
                third-party sites or services.
              </p>
            </section>

            <section>
              <h2>Security</h2>
              <p>
                We value your trust in providing us your Personal Information, thus we are striving to use 
                commercially acceptable means of protecting it. But remember that no method of transmission over 
                the internet, or method of electronic storage is 100% secure and reliable, and we cannot guarantee 
                its absolute security.
              </p>
            </section>

            <section>
              <h2>Children's Privacy</h2>
              <p>
                These Services do not address anyone under the age of 13. We do not knowingly collect personally 
                identifiable information from children under 13 years of age. In the case we discover that a child 
                under 13 has provided us with personal information, we immediately delete this from our servers. If 
                you are a parent or guardian and you are aware that your child has provided us with personal 
                information, please contact us so that we will be able to do the necessary actions.
              </p>
            </section>

            <section>
              <h2>Changes to This Privacy Policy</h2>
              <p>
                We may update our Privacy Policy from time to time. Thus, you are advised to review this page 
                periodically for any changes. We will notify you of any changes by posting the new Privacy Policy 
                on this page.
              </p>

              <p>This policy is effective as of 2023-03-27</p>
            </section>

            <section>
              <h2>Contact Us</h2>
              <p>
                If you have any questions or suggestions about our Privacy Policy, do not hesitate to contact us at{" "}
                <a 
                  href="mailto:hey@reelmatch.app"
                  className="text-reelmatch-primary hover:underline"
                >
                  hey@reelmatch.app
                </a>.
              </p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
